import { useState } from 'react';
import { highlightedLabs, labFilters } from '@/mocks/labs';

const difficultyColor: Record<string, string> = {
  Beginner: 'bg-emerald-500/20 text-emerald-400',
  Intermediate: 'bg-amber-500/20 text-amber-400',
  Advanced: 'bg-red-500/20 text-red-400',
};

const tagColor: Record<string, string> = {
  Popular: 'bg-[#00D9FF]/20 text-[#00D9FF]',
  New: 'bg-emerald-500/20 text-emerald-400',
  Hot: 'bg-red-500/20 text-red-400',
  Beginner: 'bg-purple-500/20 text-purple-400',
};

const HighlightedLabsSection = () => {
  const [activeFilter, setActiveFilter] = useState('All');

  const filtered =
    activeFilter === 'All'
      ? highlightedLabs
      : highlightedLabs.filter((l) => l.difficulty === activeFilter || l.tag === activeFilter);

  const [featured, ...rest] = filtered;

  return (
    <section id="highlighted-labs" className="py-24 bg-[#0A1628]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="inline-block text-[#00D9FF] text-xs font-bold tracking-widest uppercase mb-3">
              Featured Content
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white">
              Highlighted Security Labs
            </h2>
          </div>
          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            {labFilters.map((f) => (
              <button
                key={f}
                onClick={() => setActiveFilter(f)}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeFilter === f
                    ? 'bg-[#00D9FF] text-[#0A1628]'
                    : 'border border-white/20 text-gray-400 hover:border-[#00D9FF] hover:text-[#00D9FF]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">No labs match this filter.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
            {/* Featured large card */}
            {featured && (
              <div className="lg:col-span-3 relative rounded-2xl overflow-hidden group cursor-pointer">
                <div className="w-full h-[320px] md:h-[420px]">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/50 to-transparent"></div>
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${difficultyColor[featured.difficulty]}`}>
                    {featured.difficulty}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${tagColor[featured.tag] ?? 'bg-white/20 text-white'}`}>
                    {featured.tag}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-shield-line text-[#00D9FF] text-sm"></i>
                    <span className="text-[#00D9FF] text-xs font-semibold uppercase tracking-wider">{featured.category}</span>
                  </div>
                  <h3 className="text-white font-black text-2xl md:text-3xl mb-2">{featured.title}</h3>
                  <p className="text-gray-300 text-sm mb-4 leading-relaxed">{featured.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-gray-400 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {featured.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {featured.completions} completed
                      </span>
                    </div>
                    <button className="w-10 h-10 rounded-full bg-[#00D9FF] flex items-center justify-center hover:bg-white transition-colors duration-200 cursor-pointer">
                      <i className="ri-play-fill text-[#0A1628] text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Smaller cards */}
            <div className="lg:col-span-2 flex flex-col gap-5">
              {rest.slice(0, 2).map((lab) => (
                <div
                  key={lab.id}
                  className="relative rounded-2xl overflow-hidden group cursor-pointer flex-1"
                >
                  <div className="w-full h-[180px]">
                    <img
                      src={lab.image}
                      alt={lab.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent"></div>
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${difficultyColor[lab.difficulty]}`}>
                      {lab.difficulty}
                    </span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${tagColor[lab.tag] ?? 'bg-white/20 text-white'}`}>
                      {lab.tag}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="text-white font-bold text-base mb-1">{lab.title}</h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3 text-gray-400 text-xs">
                        <span className="flex items-center gap-1">
                          <i className="ri-time-line"></i> {lab.duration}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-user-line"></i> {lab.completions}
                        </span>
                      </div>
                      <button className="w-8 h-8 rounded-full bg-[#00D9FF] flex items-center justify-center hover:bg-white transition-colors duration-200 cursor-pointer">
                        <i className="ri-play-fill text-[#0A1628] text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bottom row of remaining cards */}
        {filtered.length > 3 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
            {rest.slice(2).map((lab) => (
              <div
                key={lab.id}
                className="bg-[#0F2A4A] rounded-2xl overflow-hidden group cursor-pointer hover:-translate-y-1 transition-transform duration-300"
              >
                <div className="w-full h-[160px]">
                  <img
                    src={lab.image}
                    alt={lab.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${difficultyColor[lab.difficulty]}`}>
                      {lab.difficulty}
                    </span>
                    <span className="text-gray-500 text-xs">{lab.category}</span>
                  </div>
                  <h3 className="text-white font-bold text-base mb-2">{lab.title}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed mb-4">{lab.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-gray-500 text-xs">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {lab.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-user-line"></i> {lab.completions}
                      </span>
                    </div>
                    <button className="px-4 py-1.5 rounded-full bg-[#00D9FF]/10 text-[#00D9FF] text-xs font-semibold hover:bg-[#00D9FF] hover:text-[#0A1628] transition-colors duration-200 cursor-pointer whitespace-nowrap">
                      Start Lab
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <button className="px-8 py-3 rounded-full border border-white/20 text-gray-300 text-sm font-semibold hover:border-[#00D9FF] hover:text-[#00D9FF] transition-all duration-200 cursor-pointer whitespace-nowrap">
            View All 500+ Labs <i className="ri-arrow-right-line ml-1"></i>
          </button>
        </div>
      </div>
    </section>
  );
};

export default HighlightedLabsSection;
